package dev.mobilearchitecture.web.pages

import androidx.compose.runtime.Composable
import androidx.compose.runtime.SideEffect
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.add
import com.varabyte.kobweb.core.init.InitRoute
import androidx.compose.runtime.LaunchedEffect
import dev.mobilearchitecture.web.initVanta
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.forms.Button
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.vh
import org.jetbrains.compose.web.css.vw
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.dom.*
import dev.mobilearchitecture.web.components.layouts.PageLayoutData
import dev.mobilearchitecture.web.components.sections.SessionOptions
import dev.mobilearchitecture.web.toSitePalette
import kotlinx.browser.document
import com.varabyte.kobweb.compose.css.boxShadow

import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.JustifyContent
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.css.Animation
import com.varabyte.kobweb.silk.style.animation.Keyframes
import com.varabyte.kobweb.silk.style.animation.toAnimation
import com.varabyte.kobweb.compose.ui.styleModifier
import org.jetbrains.compose.web.css.ms
import org.jetbrains.compose.web.css.keywords.auto

val FadeInUp = Keyframes {
    0.percent {
        Modifier
            .opacity(0)
            .styleModifier { property("transform", "translateY(20px)") }
    }
    100.percent {
        Modifier
            .opacity(1)
            .styleModifier { property("transform", "translateY(0)") }
    }
}

val HeroContainerStyle = CssStyle {
    base { Modifier.fillMaxWidth().padding(topBottom = 4.cssRem, leftRight = 2.cssRem).margin(top = 4.cssRem) }
    Breakpoint.MD { Modifier.margin { top(8.vh) } }
}

val SectionContainerStyle = CssStyle.base {
    Modifier
        .fillMaxWidth()
        .padding(topBottom = 4.cssRem, leftRight = 2.cssRem)
        .styleModifier { property("margin-left", "auto"); property("margin-right", "auto") }
}

val CardStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .padding(2.cssRem)
            .borderRadius(1.cssRem)
            .backgroundColor(com.varabyte.kobweb.compose.ui.graphics.Color.rgb(0xFFFFFF)) // Solid white for card
            .boxShadow(offsetX = 0.px, offsetY = 4.px, blurRadius = 6.px, spreadRadius = (-1).px, color = com.varabyte.kobweb.compose.ui.graphics.Color.rgba(0, 0, 0, 0.1f)) // Lighter shadow
            .margin(bottom = 2.cssRem)
            .transition(Transition.of("transform", 250.ms), Transition.of("box-shadow", 250.ms))
    }
    cssRule(":hover") {
        Modifier
            .scale(1.02)
            .boxShadow(offsetX = 0.px, offsetY = 10.px, blurRadius = 15.px, spreadRadius = (-3).px, color = com.varabyte.kobweb.compose.ui.graphics.Color.rgba(0, 0, 0, 0.15f))
    }
}

val FaqItemStyle = CssStyle {
    base {
        Modifier
            .margin(bottom = 1.cssRem)
            .padding(1.5.cssRem)
            .borderRadius(0.75.cssRem)
            .backgroundColor(com.varabyte.kobweb.compose.ui.graphics.Color.rgb(0xFFFFFF)) // Solid white for FAQ items
            .boxShadow(offsetX = 0.px, offsetY = 1.px, blurRadius = 2.px, spreadRadius = 0.px, color = com.varabyte.kobweb.compose.ui.graphics.Color.rgba(0, 0, 0, 0.1f))
            .transition(Transition.of("transform", 200.ms), Transition.of("box-shadow", 200.ms))
    }
    cssRule(":hover") {
        Modifier
            .scale(1.01)
            .boxShadow(offsetX = 0.px, offsetY = 4.px, blurRadius = 8.px, spreadRadius = 0.px, color = com.varabyte.kobweb.compose.ui.graphics.Color.rgba(0, 0, 0, 0.15f))
    }
}

fun injectJsonLd(id: String, json: String) {
    val document = kotlinx.browser.document
    if (document.getElementById(id) != null) return // avoid duplicates

    val script = document.createElement("script").apply {
        setAttribute("type", "application/ld+json")
        setAttribute("id", id)
        textContent = json
    }
    document.head?.appendChild(script)
}

val personJsonLd = """
{
  "@context": "https://schema.org",
  "@type": "Person",
  "name": "Prasad Pawar",
  "jobTitle": "Mobile Engineer, App & SDK Builder, Founder of RetterPlus",
  "description": "11+ years of industry experience building mobile apps and SDKs. Founder of RetterPlus (retterplus.com). Expert in mobile system design, clean architecture, MVVM, Jetpack Compose, and interview preparation for top tech companies.",
  "url": "https://mobilearchitecture.dev",
  "sameAs": [
    "https://www.linkedin.com/in/pawarprasad37"
  ],
  "knowsAbout": [
    "Mobile System Design",
    "Mobile Architecture",
    "Jetpack Compose",
    "Clean Architecture",
    "MVVM",
    "Kotlin Multiplatform",
    "Mobile Interview Preparation"
  ]
}
"""

val serviceJsonLd = """
{
  "@context": "https://schema.org",
  "@type": "Service",
  "name": "Mobile System Design Mock Interview",
  "description": "A 90-minute 1:1 session covering a full mobile system design mock interview followed by personalised, detailed feedback. For Android and iOS developers at any career level, and engineering leaders evaluating or consulting on mobile system design interviews.",
  "provider": {
    "@type": "Person",
    "name": "Prasad Pawar",
    "url": "https://mobilearchitecture.dev"
  },
  "areaServed": "Worldwide",
  "audience": {
    "@type": "Audience",
    "audienceType": "Android and iOS developers at any experience level, and engineering managers and leaders consulting on mobile system design interview processes"
  },
  "url": "https://mobilearchitecture.dev/#book"
}
"""

val faqPageJsonLd = """
{
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "What is mobile system design?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Mobile system design is the practice of designing scalable, maintainable architectures for mobile applications. It covers architecture patterns (MVVM, Clean Architecture), data management (caching, offline-first), networking, modularisation, and performance from the mobile client's perspective. In interviews, candidates are asked to design a real-world app or SDK end-to-end, demonstrating breadth of knowledge and depth of reasoning."
      }
    },
    {
      "@type": "Question",
      "name": "What is the difference between mobile system design and backend system design?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Backend system design focuses on servers, databases, load balancing, and distributed systems. Mobile system design focuses on the client — how the app is structured, how it manages state, how it handles network failures, how it caches data, and how it balances performance constraints unique to mobile devices such as battery, memory, and connectivity. Both may appear in the same interview loop at top tech companies, but they test entirely different skills."
      }
    },
    {
      "@type": "Question",
      "name": "What companies ask mobile system design interview questions?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Top tech companies including Google, Meta, Apple, Uber, Airbnb, Flipkart, Swiggy, PhonePe, Razorpay, CRED, and Meesho include a mobile system design round for Senior Engineer and above roles. The round is increasingly common at the L5/L6 equivalent level in India and globally. Developers at any career level targeting these roles — as well as engineering leaders evaluating interview processes — benefit from structured preparation."
      }
    },
    {
      "@type": "Question",
      "name": "What topics are covered in a mobile system design interview?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "A mobile system design interview covers client architecture (MVVM, Clean Architecture), offline-first data strategies, networking and API design, caching, pagination, background processing, modularisation, SDK design, performance optimisation, and UI architecture. Interviewers also probe cross-cutting concerns: security, error handling, accessibility, and analytics. The focus depends on the company — a payments company will probe security deeply; a social app will probe feed architecture."
      }
    },
    {
      "@type": "Question",
      "name": "What is the best resource for mobile system design interview preparation?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "The main publicly available resources are a course on Educative and a couple of books which become outdated very quickly. These are useful introductions but have coverage gaps — particularly around SDK design, offline-first conflict resolution, and the practical tradeoffs interviewers probe in real interviews. Expert-led 1:1 mock interviews with personalised feedback provide the missing depth that no written resource can replicate."
      }
    },
    {
      "@type": "Question",
      "name": "How is a mobile system design interview different from a coding interview?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "A coding interview tests algorithmic problem-solving under time pressure. A mobile system design interview tests architectural thinking — the ability to decompose a complex problem, justify technology choices, reason about tradeoffs, and communicate a coherent design. There is no single correct answer; the interviewer evaluates how you think, not just what you know. Strong candidates ask clarifying questions, define scope, and proactively address tradeoffs."
      }
    },
    {
      "@type": "Question",
      "name": "How long is a typical mobile system design interview round?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "A typical mobile system design round lasts 45 to 60 minutes. Candidates spend roughly 5 minutes clarifying requirements, 10 to 15 minutes proposing high-level architecture, and 20 to 30 minutes deep-diving into 2 to 3 specific components with the interviewer. A mock interview session of 90 minutes gives you a full round plus debrief time, which is the most effective format for preparation."
      }
    },
    {
      "@type": "Question",
      "name": "What is the difference between designing an app and designing a library in a mobile system design interview?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Designing an app focuses on client architecture, data flows, and user-facing feature implementation. Designing an SDK or library focuses on public API design, internal caching, extensibility, and minimal dependency footprint. Both types appear frequently. SDK design is underrepresented in available study resources but is increasingly common in senior-level interviews, especially at companies that ship developer-facing products."
      }
    },
    {
      "@type": "Question",
      "name": "What is offline-first architecture in Android/iOS?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Offline-first architecture means the app reads from and writes to a local database (Room) as its primary data source, syncing with the server as a secondary operation. The UI never waits for a network call. This requires careful design of sync strategies, conflict resolution (last-write-wins, CRDTs, or server-authoritative models), and WorkManager scheduling. It is a frequently asked mobile system design topic that reveals deep understanding of mobile-specific constraints."
      }
    },
    {
      "@type": "Question",
      "name": "How do I design a scalable Android/iOS app architecture?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "A scalable Android/iOS app architecture separates concerns into three layers: data (repositories, data sources, Room, Retrofit), domain (use cases, business logic, pure Kotlin), and presentation (ViewModels, UI state, Compose or Views). Dependencies flow inward — the domain layer has no Android dependencies. Feature modules depend on shared modules, not on each other. Dependency injection with Hilt wires everything together."
      }
    },
    {
      "@type": "Question",
      "name": "Who should book a 1:1 mobile system design mock session?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "This session is for Android or iOS developers at any career level who are actively interviewing or preparing for system design rounds, as well as engineering leaders who want to evaluate, consult on, or improve mobile architecture interviews at their organisation. It is particularly valuable for anyone who has studied patterns in theory but has never had their thinking challenged in a real mock environment."
      }
    },
    {
      "@type": "Question",
      "name": "What should I prepare before booking a 1:1 mobile system design session?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "No specific preparation is required — the session assesses where you are right now and gives you a clear picture of where you need to go. Having a basic familiarity with MVVM, the Repository pattern, and one architecture case study will make the debrief more actionable. You do not need to have finished any specific course or book beforehand."
      }
    }
  ]
}
""".trimIndent()

@InitRoute
fun initHomePage(ctx: InitRouteContext) {
    ctx.data.add(PageLayoutData("Home"))
}

@Page
@Layout(".components.layouts.PageLayout")
@Composable
fun HomePage() {
    SideEffect {
        injectJsonLd("person-schema", personJsonLd)
        injectJsonLd("service-schema", serviceJsonLd)
        injectJsonLd("faq-schema", faqPageJsonLd)
    }

    LaunchedEffect(Unit) {
        initVanta()
    }

    Main(attrs = { id("main-content") }) {

        // --- Hero Section ---
        Section(attrs = { id("hero-vanta-bg"); attr("aria-labelledby", "hero-heading"); style { property("padding-top", "0rem") } }) {
            Column(HeroContainerStyle.toModifier().animation(FadeInUp.toAnimation(duration = 800.ms)), horizontalAlignment = Alignment.CenterHorizontally) {
                H3(attrs = {
                    id("hero-heading")
                    style {
                        property("font-size", "0.875rem")
                        property("margin-top", "0")
                        property("margin-bottom", "1rem")
                        property("text-align", "center")
                        property("color", "#f472b6") // Pink color matching tech spec
                        property("text-transform", "uppercase")
                        property("letter-spacing", "0.1em")
                        property("font-weight", "bold")
                    }
                }) {
                    Text("Mobile Architecture for Developers")
                }

                H1(attrs = {
                    id("hero-subheading")
                    style {
                        property("font-size", "2.5rem")
                        property("margin-bottom", "1rem")
                        property("text-align", "center")
                        property("color", "#F8FAFC") // Explicitly set hero text to light color since default is now light mode
                    }
                }) {
                    Text("Expert-Led 1:1 Mentorship & Mock Interviews")
                }

                P(attrs = { style {
                    property("font-size", "1.25rem")
                    property("max-width", "800px")
                    property("text-align", "center")
                    property("margin-bottom", "0.5rem")
                    property("color", "#F8FAFC")
                } }) {
                    Text("Master mobile architecture, get unblocked on complex SDK designs, or ace your next system design interview. Walk in prepared with 11+ years of experience in your corner.")
                }

                val ctx = rememberPageContext()

                // --- Session Options Section ---
                SessionOptions()
            }
        }

        // --- Problem Section ---
        Section(attrs = { id("problem"); style { property("background-color", "#F8FAFC") } }) {
            Column(SectionContainerStyle.toModifier().maxWidth(800.px)) {
                H2(attrs = { style { property("margin-bottom", "1rem"); property("font-size", "2rem") } }) {
                    Text("Why Existing Resources Leave You Underprepared")
                }
                P { Text("There are currently only two widely cited resources for mobile system design interview preparation: a course on Educative and a couple of books which become outdated very quickly. While these are useful starting points, neither was created by someone who has recently conducted or been through mobile architecture rounds at top tech companies.") }
                P { Text("These resources focus heavily on high-level patterns. They undercover practical tradeoffs that interviewers probe — such as when to use WorkManager vs. a custom sync engine, how to handle offline-first data conflicts, or how to design a modular feature SDK that can be shipped as an AAR. These gaps leave candidates confident in theory but unprepared in the room.") }
                P { Text("Mock interviews are irreplaceable because they simulate the pressure, the follow-up questions, and the real-time thinking that no written resource can replicate. Knowing a pattern in the abstract is very different from articulating it clearly under the challenge of a live interview.") }
            }
        }

        // --- About Section ---
        Section(attrs = { id("about"); style { property("background-color", "#FFFFFF") } }) {
            Column(SectionContainerStyle.toModifier().maxWidth(800.px)) {
                val currentPrimaryColor = ColorMode.current.toSitePalette().brand.primary.toString()

                H2(attrs = { style { property("margin-bottom", "1rem"); property("font-size", "2rem") } }) {
                    Text("About Prasad Pawar — 11+ Years Building Mobile Apps & SDKs")
                }
                P { Text("Prasad Pawar has 11+ years of industry experience building mobile apps and SDKs. He is the founder of RetterPlus (retterplus.com), a consumer Android app he built and published to the Play Store. He currently works at Razorpay, where he focuses on large-scale mobile architecture and payment SDK design.") }
                P { Text("His experience spans clean architecture with MVVM and MVP, Jetpack Compose UI systems, dependency injection with Hilt, offline-first data architectures, and modular Android/iOS app design.") }
                A(href = "https://www.linkedin.com/in/pawarprasad37", attrs = {
                    attr("target", "_blank")
                    style {
                        property("display", "inline-block")
                        property("margin-top", "1rem")
                        property("color", currentPrimaryColor)
                        property("font-weight", "bold")
                        property("text-decoration", "none")
                    }
                }) {
                    Text("View Full Profile on LinkedIn →")
                }
            }
        }

        // --- How It Works Section ---
        Section(attrs = { id("session"); style { property("background-color", "#F8FAFC") } }) {
            Column(SectionContainerStyle.toModifier().maxWidth(800.px)) {
                H2(attrs = { style { property("margin-bottom", "1rem"); property("font-size", "2rem") } }) {
                    Text("What Happens in Your 90-Minute Session")
                }

                Ol(attrs = { style { property("padding-left", "1.5rem") } }) {
                    Li(attrs = { style { property("margin-bottom", "1rem") } }) {
                        B { Text("The Mock Interview — 60 min") }
                        P(attrs = { style { property("margin-top", "0.5rem") } }) { Text("You receive a real system design problem: design an app, a library, or a specific component. The session runs exactly like a real interview — requirements clarification, architecture proposal, deep-dive questions, tradeoff discussion. No hand-holding; you are treated as a candidate.") }
                    }
                    Li(attrs = { style { property("margin-bottom", "1rem") } }) {
                        B { Text("The Live Debrief — 20 min") }
                        P(attrs = { style { property("margin-top", "0.5rem") } }) { Text("After the mock, a detailed walkthrough of what you did well, what was missing, and what a model answer looks like for that problem. Concrete, actionable, and honest.") }
                    }
                    Li(attrs = { style { property("margin-bottom", "1rem") } }) {
                        B { Text("Written Feedback — Delivered Async") }
                        P(attrs = { style { property("margin-top", "0.5rem") } }) { Text("A structured written summary sent after the session: strengths identified, gaps to close, specific resources to study, and a personalised preparation roadmap for your target companies.") }
                    }
                }
            }
        }

        // --- Topics Section ---
        Section(attrs = { id("topics"); style { property("background-color", "#FFFFFF") } }) {
            Column(SectionContainerStyle.toModifier().maxWidth(800.px)) {
                H2(attrs = { style { property("margin-bottom", "1rem"); property("font-size", "2rem") } }) {
                    Text("Topics the Mock Interview May Cover")
                }
                P(attrs = { style { property("margin-bottom", "2rem") } }) {
                    Text("Mobile system design interviews test a wide range of skills. Sessions cover the full breadth of what top interviewers actually assess — not just textbook patterns.")
                }

                Column(CardStyle.toModifier().backgroundColor(ColorMode.current.toSitePalette().nearBackground)) {
                    H3(attrs = { style { property("margin-top", "0"); property("margin-bottom", "0.5rem") } }) { Text("Architecture & Design Patterns") }
                    Ul(attrs = { style { property("padding-left", "1.5rem"); property("margin-bottom", "0") } }) {
                        Li { Text("MVVM, MVP, MVI — when and why to choose each") }
                        Li { Text("Clean Architecture layering (data, domain, presentation)") }
                        Li { Text("Repository pattern and data source abstraction") }
                        Li { Text("Unidirectional data flow in Compose and traditional View systems") }
                    }
                }

                Column(CardStyle.toModifier().backgroundColor(ColorMode.current.toSitePalette().nearBackground)) {
                    H3(attrs = { style { property("margin-top", "0"); property("margin-bottom", "0.5rem") } }) { Text("Offline-First & Data Management") }
                    Ul(attrs = { style { property("padding-left", "1.5rem"); property("margin-bottom", "0") } }) {
                        Li { Text("Designing offline-first apps with Room and sync strategies") }
                        Li { Text("Conflict resolution for local-first data models") }
                        Li { Text("Caching strategies: LRU, TTL, write-through vs. write-back") }
                        Li { Text("Background sync with WorkManager vs. custom solutions") }
                    }
                }

                Column(CardStyle.toModifier().backgroundColor(ColorMode.current.toSitePalette().nearBackground)) {
                    H3(attrs = { style { property("margin-top", "0"); property("margin-bottom", "0.5rem") } }) { Text("Networking & API Design") }
                    Ul(attrs = { style { property("padding-left", "1.5rem"); property("margin-bottom", "0") } }) {
                        Li { Text("REST vs. WebSocket — when to use each in mobile apps") }
                        Li { Text("Pagination strategies: cursor-based, page-based, keyset") }
                        Li { Text("Retry logic, exponential backoff, and circuit breaker patterns") }
                        Li { Text("OkHttp interceptors and custom networking layers") }
                    }
                }

                Column(CardStyle.toModifier().backgroundColor(ColorMode.current.toSitePalette().nearBackground)) {
                    H3(attrs = { style { property("margin-top", "0"); property("margin-bottom", "0.5rem") } }) { Text("Performance & Scalability") }
                    Ul(attrs = { style { property("padding-left", "1.5rem"); property("margin-bottom", "0") } }) {
                        Li { Text("App startup time optimisation and baseline profiles") }
                        Li { Text("Memory management and avoiding common leaks") }
                        Li { Text("Battery-aware design for background tasks") }
                        Li { Text("Handling varied network conditions (2G, 3G, Wi-Fi)") }
                    }
                }

                Column(CardStyle.toModifier().backgroundColor(ColorMode.current.toSitePalette().nearBackground)) {
                    H3(attrs = { style { property("margin-top", "0"); property("margin-bottom", "0.5rem") } }) { Text("Modularisation & SDK Design") }
                    Ul(attrs = { style { property("padding-left", "1.5rem"); property("margin-bottom", "0") } }) {
                        Li { Text("Designing a modular multi-module Android/iOS project") }
                        Li { Text("Designing a reusable SDK (image loader, analytics, file downloader)") }
                        Li { Text("Feature flags and dynamic delivery") }
                        Li { Text("Dependency injection across module boundaries with Hilt") }
                    }
                }

                Column(CardStyle.toModifier().backgroundColor(ColorMode.current.toSitePalette().nearBackground)) {
                    H3(attrs = { style { property("margin-top", "0"); property("margin-bottom", "0.5rem") } }) { Text("UI Architecture") }
                    Ul(attrs = { style { property("padding-left", "1.5rem"); property("margin-bottom", "0") } }) {
                        Li { Text("Designing a scalable design system in Jetpack Compose") }
                        Li { Text("State management — ViewModel state vs. UI state vs. local state") }
                        Li { Text("Navigation patterns in multi-module apps") }
                    }
                }

                Column(CardStyle.toModifier().backgroundColor(ColorMode.current.toSitePalette().nearBackground)) {
                    H3(attrs = { style { property("margin-top", "0"); property("margin-bottom", "0.5rem") } }) { Text("Cross-Cutting Concerns") }
                    Ul(attrs = { style { property("padding-left", "1.5rem"); property("margin-bottom", "0") } }) {
                        Li { Text("Security: certificate pinning, encryption, secure storage") }
                        Li { Text("Accessibility considerations in architecture") }
                        Li { Text("Error handling strategies and graceful degradation") }
                        Li { Text("Analytics and instrumentation design") }
                    }
                }
            }
        }

        // --- FAQ Section ---
        Section(attrs = { id("faq"); style { property("background-color", "#F8FAFC") } }) {
            Column(SectionContainerStyle.toModifier().maxWidth(800.px)) {
                H2(attrs = { style { property("margin-bottom", "1.5rem"); property("font-size", "2rem") } }) {
                    Text("FAQ About 1:1 Sessions & Mentorship")
                }

                val faqs = listOf(
                    "What can I use a 15-minute session for?" to "These are high-intensity \"unblocking\" sessions. Use them for resume reviews, salary negotiation advice, or showing me a specific block of code you're struggling to modularize.",
                    "Do I need to be preparing for an interview?" to "Not at all. The 15 and 30-minute sessions are designed for working engineers and leaders who need an external expert opinion on their day-to-day architecture challenges.",
                    "What is mobile system design?" to "Mobile system design is the practice of designing scalable, maintainable architectures for mobile applications. It covers architecture patterns (MVVM, Clean Architecture), data management (caching, offline-first), networking, modularisation, and performance from the mobile client's perspective. In interviews, candidates are asked to design a real-world app or SDK end-to-end, demonstrating breadth of knowledge and depth of reasoning.",
                    "What is the difference between mobile system design and backend system design?" to "Backend system design focuses on servers, databases, load balancing, and distributed systems. Mobile system design focuses on the client — how the app is structured, how it manages state, how it handles network failures, how it caches data, and how it balances performance constraints unique to mobile devices such as battery, memory, and connectivity. Both may appear in the same interview loop at top tech companies, but they test entirely different skills.",
                    "What companies ask mobile system design interview questions?" to "Top tech companies including Google, Meta, Apple, Uber, Airbnb, Flipkart, Swiggy, PhonePe, Razorpay, CRED, and Meesho include a mobile system design round for Senior Engineer and above roles. The round is increasingly common at the L5/L6 equivalent level in India and globally. Developers at any career level targeting these roles — as well as engineering leaders evaluating interview processes — benefit from structured preparation.",
                    "What topics are covered in a mobile system design interview?" to "A mobile system design interview covers client architecture (MVVM, Clean Architecture), offline-first data strategies, networking and API design, caching, pagination, background processing, modularisation, SDK design, performance optimisation, and UI architecture. Interviewers also probe cross-cutting concerns: security, error handling, accessibility, and analytics. The focus depends on the company — a payments company will probe security deeply; a social app will probe feed architecture.",
                    "What is the best resource for mobile system design interview preparation?" to "The main publicly available resources are a course on Educative and a couple of books which become outdated very quickly. These are useful introductions but have coverage gaps — particularly around SDK design, offline-first conflict resolution, and the practical tradeoffs interviewers probe in real interviews. Expert-led 1:1 mock interviews with personalised feedback provide the missing depth that no written resource can replicate.",
                    "How is a mobile system design interview different from a coding interview?" to "A coding interview tests algorithmic problem-solving under time pressure. A mobile system design interview tests architectural thinking — the ability to decompose a complex problem, justify technology choices, reason about tradeoffs, and communicate a coherent design. There is no single correct answer; the interviewer evaluates how you think, not just what you know. Strong candidates ask clarifying questions, define scope, and proactively address tradeoffs.",
                    "How long is a typical mobile system design interview round?" to "A typical mobile system design round lasts 45 to 60 minutes. Candidates spend roughly 5 minutes clarifying requirements, 10 to 15 minutes proposing high-level architecture, and 20 to 30 minutes deep-diving into 2 to 3 specific components with the interviewer. A mock interview session of 90 minutes gives you a full round plus debrief time, which is the most effective format for preparation.",
                    "What is the difference between designing an app and designing a library in a mobile system design interview?" to "Designing an app focuses on client architecture, data flows, and user-facing feature implementation. Designing an SDK or library focuses on public API design, internal caching, extensibility, and minimal dependency footprint. Both types appear frequently. SDK design is underrepresented in available study resources but is increasingly common in senior-level interviews, especially at companies that ship developer-facing products.",
                    "What is offline-first architecture in Android/iOS?" to "Offline-first architecture means the app reads from and writes to a local database (Room) as its primary data source, syncing with the server as a secondary operation. The UI never waits for a network call. This requires careful design of sync strategies, conflict resolution (last-write-wins, CRDTs, or server-authoritative models), and WorkManager scheduling. It is a frequently asked mobile system design topic that reveals deep understanding of mobile-specific constraints.",
                    "How do I design a scalable Android/iOS app architecture?" to "A scalable Android/iOS app architecture separates concerns into three layers: data (repositories, data sources, Room, Retrofit), domain (use cases, business logic, pure Kotlin), and presentation (ViewModels, UI state, Compose or Views). Dependencies flow inward — the domain layer has no Android dependencies. Feature modules depend on shared modules, not on each other. Dependency injection with Hilt wires everything together.",
                    "Who should book a 1:1 mobile system design mock session?" to "This session is for Android or iOS developers at any career level who are actively interviewing or preparing for system design rounds, as well as engineering leaders who want to evaluate, consult on, or improve mobile architecture interviews at their organisation. It is particularly valuable for anyone who has studied patterns in theory but has never had their thinking challenged in a real mock environment.",
                    "What should I prepare before booking a 1:1 mobile system design session?" to "No specific preparation is required — the session assesses where you are right now and gives you a clear picture of where you need to go. Having a basic familiarity with MVVM, the Repository pattern, and one architecture case study will make the debrief more actionable. You do not need to have finished any specific course or book beforehand."
                )

                faqs.forEach { (q, a) ->
                    Column(FaqItemStyle.toModifier().backgroundColor(ColorMode.current.toSitePalette().nearBackground)) {
                        P(attrs = { style { property("font-weight", "bold"); property("margin-bottom", "0.5rem") } }) {
                            Text("Q: $q")
                        }
                        P { Text(a) }
                    }
                }
            }
        }

        // --- Final CTA Section ---
        Section(attrs = { id("book"); style { property("text-align", "center"); property("background-color", "#FFFFFF") } }) {
            Column(SectionContainerStyle.toModifier().maxWidth(800.px).styleModifier { property("margin-left", "auto"); property("margin-right", "auto") }, horizontalAlignment = Alignment.CenterHorizontally) {
                H2(attrs = { style { property("margin-bottom", "1rem"); property("font-size", "2rem"); property("text-align", "center") } }) {
                    Text("Ready to Walk Into Your Next Interview Confidently?")
                }

                P(attrs = { style { property("margin-bottom", "2rem"); property("text-align", "center") } }) {
                    Text("Limited sessions are available each week. Each session is 90 minutes and includes the mock interview, live debrief, and written personalised feedback report sent afterward.")
                }

                Button(
                    onClick = { kotlinx.browser.window.open("https://mobilearchitecture.setmore.com/services", "_blank") },
                    variant = dev.mobilearchitecture.web.PrimaryButtonVariant,
                    modifier = Modifier.backgroundColor(ColorMode.current.toSitePalette().brand.primary).color(Colors.White)
                ) {
                    Text("Book Your 1:1 Session →")
                }

                Ul(attrs = {
                    style {
                        property("list-style", "none")
                        property("padding", "0")
                        property("margin-top", "2rem")
                        property("display", "inline-block")
                        property("text-align", "left")
                        property("color", "#666")
                    }
                }) {
                    Li { Text("✓ 90-minute session") }
                    Li { Text("✓ Real mock interview format") }
                    Li { Text("✓ Written feedback delivered afterward") }
                    Li { Text("✓ Open to engineers at any career level") }
                    Li { Text("✓ Engineering leadership consulting available") }
                    Li { Text("✓ Covers Android & iOS architecture patterns") }
                    Li { Text("✓ Targets all major company interview styles") }
                }
            }
        }
    }
}
